import React from "react";
import Link from "next/link";
import { Check, CircleDotDashed, CircleEllipsis, Clock4 } from "lucide-react";
import { Button, ButtonVariantTypes } from "components/ui/button";
import { Badge } from "components/ui/badge";

export type Column = {
  title: string;
  type?: ColumnType;
  className?: string;
};

type StatusDetails = {
  icon?: React.ReactElement;
  label: string;
};

export const formatNumber = (value: any, format?: string): string => {
  const numValue = Number(value);
  if (isNaN(numValue)) return value; // Return the original value if it's not a number
  switch (format) {
    case "currency":
      return `$${numValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "plain":
    default:
      return numValue.toLocaleString();
  }
};

type StatusKey = "Done" | "In Progress" | "Pending" | "Not Started" | "Incomplete";

const statusMap: Record<StatusKey, StatusDetails> = {
  Done: { icon: <Check size="20" color="#08875D" />, label: "Done" },
  "In Progress": { icon: <CircleEllipsis size="20" color="#E5B04F" />, label: "In Progress" },
  Pending: { icon: <CircleDotDashed size="20" color="#E5B04F" />, label: "Pending" },
  "Not Started": { label: "Not Started" },
  Incomplete: { icon: <Clock4 size="20" color="gray" />, label: "Incomplete" }
};

export type ColumnType = "text" | "badge" | "link" | "status" | "number";

export const renderCellContent = (
  type: ColumnType,
  value: any,
  row: Record<string, any>,
  format?: string
): React.ReactNode => {
  if (value == null) return "-";
  switch (type) {
    case "number":
      return format === "currency" ? formatNumber(value, format) : value;
    case "badge":
      return <Badge isFromColumns>{value}</Badge>;
    case "link":
      return (
        <Link href={value.href}>
          <Button variant={ButtonVariantTypes.LINK} className="pl-0 font-matter">
            {value.text}
          </Button>
        </Link>
      );
    case "status":
      const statusKey = value as StatusKey;
      const statusDetails = statusMap[statusKey];
      if (statusDetails) {
        return (
          <div className="inline-flex items-center justify-center align-middle">
            {statusDetails.icon && statusDetails.icon} {/* Check if icon exists */}
            <span className="ml-2">{statusDetails.label}</span>
          </div>
        );
      }
      return value; // Fallback if the status value is not recognized
    default:
      return value; // Renders as plain text by default
  }
};
